import React from "react"
import {useParams} from "react-router-dom"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {useAccessToken} from "../../services/auth"
import {getManagedSolutionsAccount} from "../../resources/managedSolutions"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import TitleBar from "../../components/TitleBar/TitleBar"
import {MainSection} from "../../containers/MainSection/MainSection"
import ManagedSolutionsTransactions from "../../components/ManagedSolutionsTransactions/ManagedSolutionsTransactions"
import {startCase} from "lodash"
import moment from "moment"
import HorizontalField, {OptionalHorizontalField} from "../../components/HorizontalField/HorizontalField"
import {accountNumberPreview, InformationHiding} from "../../components/InformationHiding/InformationHiding"
import {useIsBankUser, useIsOrgManagedSolutionsOperationalAdminOnly} from "../../services/auth"
import numeral from "numeral"
import TagsViewer from "../../components/Tags/TagsViewer"
import {hasPermission} from "../../services/permission"

export default function ManagedSolutionsAccountPage() {
	const {accountId = ""} = useParams<{accountId: string}>()
	const accessToken = useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const asyncResult = useAsyncResult(
		() => getManagedSolutionsAccount(accessToken, accountId),
		[refreshToken, accountId]
	)
	const isBankUser = useIsBankUser()
	const isOperationalAdminOnly = useIsOrgManagedSolutionsOperationalAdminOnly()
	const hasTransactionPermission = hasPermission("rtl.transaction", "get")

	return (
		<>
			<AsyncResultComponent asyncResult={asyncResult} refresh={refresh}>
				{({value: account}) => {
					const {attributes} = account.data
					const accountTypeDisplay = attributes.accountType ? startCase(attributes.accountType) : undefined
					const statusDisplay = startCase(attributes.status)
					const closeReasonDisplay = attributes.closeReason ? startCase(attributes.closeReason) : undefined
					const createdAt = attributes.createdAt ? moment(attributes.createdAt).format("L LT") : undefined
					const balance = attributes.balance
					const isOperationalAccount = attributes.tags?.purpose === "operational_account"
					const showBalance = !isOperationalAdminOnly || isOperationalAccount
					// Operational-admin-only users can only fetch transactions for operational accounts;
					// hide the transactions card on other accounts to avoid the backend Forbidden error.
					const showTransactions = hasTransactionPermission && (!isOperationalAdminOnly || isOperationalAccount)

					return (
						<>
							<TitleBar
								title={`Account ${account.data.id}`}
								breadcrumbs={[{text: "Accounts", path: "/managed-solutions/accounts"}]}
								meta={accountTypeDisplay ? [{text: accountTypeDisplay}] : undefined}
							/>

							<MainSection>
								{showTransactions ? (
									<div className="columns">
										<div className="column is-4">
											<div className="card">
												<div className="card-header">
													<p className="card-header-title">Account Details</p>
												</div>
												<div className="card-content">
													<OptionalHorizontalField title="ID" attribute={account.data.id} />
													<OptionalHorizontalField title="Type" attribute={accountTypeDisplay} />
													<OptionalHorizontalField title="Status" attribute={statusDisplay} />
													<OptionalHorizontalField title="Close Reason" attribute={closeReasonDisplay} />
													<OptionalHorizontalField title="Created At" attribute={createdAt} />
													{showBalance && (
														<OptionalHorizontalField
															title="Balance"
															attribute={balance !== undefined ? numeral(balance / 100).format("$0,0.00") : "—"}
															className={balance !== undefined && balance < 0 ? "has-text-danger" : ""}
														/>
													)}
													<HorizontalField label="Routing Number">
														<input type="text" className="input is-static" readOnly value={attributes.routingNumber} />
													</HorizontalField>
													<HorizontalField label="Account Number">
														<div style={{maxWidth: "400px"}}>
															<InformationHiding
																element={(accountNumber) => (
																	<input type="text" readOnly value={accountNumber} className="input is-static" />
																)}
																getValue={() => attributes.accountNumber ?? ""}
																placeholder={accountNumberPreview(attributes.accountNumber)}
																defaultIsHidden={!isBankUser}
															/>
														</div>
													</HorizontalField>
													<HorizontalField label="User IDs">
														<div>
															{attributes.userIds.map((userId, index) => (
																<div key={index} style={{marginBottom: "0.5rem"}}>
																	{userId}
																</div>
															))}
														</div>
													</HorizontalField>
												</div>
											</div>
											{Object.keys(attributes.tags || {}).length > 0 && (
												<div className="card">
													<div className="card-header">
														<p className="card-header-title">Tags</p>
													</div>
													<div className="card-content">
														<TagsViewer tags={attributes.tags} />
													</div>
												</div>
											)}
										</div>
										<div className="column">
											<ManagedSolutionsTransactions
												accountId={account.data.id}
												enableTitle={true}
												fullHeight={false}
												limit={10}
											/>
										</div>
									</div>
								) : (
									<div className="columns">
										<div className="column is-8">
											<div className="card">
												<div className="card-header">
													<p className="card-header-title">Account Details</p>
												</div>
												<div className="card-content">
													<OptionalHorizontalField title="ID" attribute={account.data.id} />
													<OptionalHorizontalField title="Type" attribute={accountTypeDisplay} />
													<OptionalHorizontalField title="Status" attribute={statusDisplay} />
													<OptionalHorizontalField title="Close Reason" attribute={closeReasonDisplay} />
													<OptionalHorizontalField title="Created At" attribute={createdAt} />
													{showBalance && (
														<OptionalHorizontalField
															title="Balance"
															attribute={balance !== undefined ? numeral(balance / 100).format("$0,0.00") : "—"}
															className={balance !== undefined && balance < 0 ? "has-text-danger" : ""}
														/>
													)}
													<HorizontalField label="Routing Number">
														<input type="text" className="input is-static" readOnly value={attributes.routingNumber} />
													</HorizontalField>
													<HorizontalField label="Account Number">
														<div style={{maxWidth: "400px"}}>
															<InformationHiding
																element={(accountNumber) => (
																	<input type="text" readOnly value={accountNumber} className="input is-static" />
																)}
																getValue={() => attributes.accountNumber ?? ""}
																placeholder={accountNumberPreview(attributes.accountNumber)}
																defaultIsHidden={!isBankUser}
															/>
														</div>
													</HorizontalField>
													<HorizontalField label="User IDs">
														<div>
															{attributes.userIds.map((userId, index) => (
																<div key={index} style={{marginBottom: "0.5rem"}}>
																	{userId}
																</div>
															))}
														</div>
													</HorizontalField>
												</div>
											</div>
										</div>
										{Object.keys(attributes.tags || {}).length > 0 && (
											<div className="column is-4">
												<div className="card">
													<div className="card-header">
														<p className="card-header-title">Tags</p>
													</div>
													<div className="card-content">
														<TagsViewer tags={attributes.tags} />
													</div>
												</div>
											</div>
										)}
									</div>
								)}
							</MainSection>
						</>
					)
				}}
			</AsyncResultComponent>
		</>
	)
}
